import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Home, MessageSquare, Mic, Menu } from 'lucide-react';
import { VoiceModal } from './VoiceModal';
import { trackEvent } from '../lib/analytics';

const MORE_PATHS = [
  '/more', '/finance', '/relations', '/advisor', '/meeting', '/ventures',
  '/personal', '/settings', '/voice-history', '/command', '/tasks',
  '/delegate', '/reminders', '/projects', '/fitness',
];

type Tab = {
  id: string;
  label: string;
  icon: typeof Home;
  match: (p: string) => boolean;
  navigate?: string;
  action?: 'capture';
};

const tabs: Tab[] = [
  {
    id: 'brief',
    navigate: '/today',
    label: 'Brief',
    match: (p) => p === '/today' || p === '/',
    icon: Home,
  },
  {
    id: 'axis',
    navigate: '/chat',
    label: 'AXIS',
    match: (p) => p === '/chat',
    icon: MessageSquare,
  },
  {
    id: 'capture',
    action: 'capture',
    label: 'Capture',
    match: () => false,
    icon: Mic,
  },
  {
    id: 'more',
    navigate: '/more',
    label: 'More',
    match: (p) => MORE_PATHS.some(mp => p.startsWith(mp)),
    icon: Menu,
  },
];

export default function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const [captureOpen, setCaptureOpen] = useState(false);

  return (
    <>
      <div
        className="fixed z-50 lumen-glass"
        style={{
          left: 12,
          right: 12,
          bottom: `calc(12px + env(safe-area-inset-bottom))`,
          borderRadius: 28,
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-around',
          padding: '0 8px',
          maxWidth: 540,
          marginLeft: 'auto',
          marginRight: 'auto',
        }}
      >
        {tabs.map((tab) => {
          const active =
            tab.match(location.pathname) ||
            (tab.action === 'capture' && captureOpen);
          const Icon = tab.icon;

          if (tab.action === 'capture') {
            return (
              <button
                key={tab.id}
                onClick={() => {
                  trackEvent('navigation_intent', { destination: 'capture', module: 'bottom_nav' });
                  setCaptureOpen(true);
                }}
                aria-label={tab.label}
                style={{
                  position: 'relative',
                  top: -18,
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, var(--gold-light), var(--gold))',
                  color: '#fff',
                  border: '4px solid #F4F1EC',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 10px 28px rgba(232, 155, 48, 0.40)',
                  flexShrink: 0,
                }}
              >
                <Icon size={22} strokeWidth={2.2} />
              </button>
            );
          }

          return (
            <button
              key={tab.id}
              onClick={() => {
                trackEvent('navigation_intent', { destination: tab.id, module: 'bottom_nav' });
                if (tab.navigate) navigate(tab.navigate);
              }}
              aria-label={tab.label}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                width: 56,
                height: 48,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                color: active ? 'var(--text)' : 'var(--subtle)',
                transition: 'color 0.18s ease',
                gap: 2,
              }}
            >
              <Icon size={22} strokeWidth={active ? 2.2 : 1.8} />
              <span
                style={{
                  fontSize: 10,
                  fontWeight: active ? 600 : 500,
                  letterSpacing: 0.1,
                  color: active ? 'var(--text)' : 'var(--subtle)',
                }}
              >
                {tab.label}
              </span>
              {active && (
                <span
                  style={{
                    position: 'absolute',
                    bottom: -4,
                    width: 4,
                    height: 4,
                    borderRadius: '50%',
                    background: 'var(--gold)',
                    boxShadow: '0 0 6px rgba(232,155,48,0.7)',
                  }}
                />
              )}
            </button>
          );
        })}
      </div>

      <VoiceModal
        isOpen={captureOpen}
        onClose={() => setCaptureOpen(false)}
      />
    </>
  );
}
